import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
export class AndroidNotificationWrapper {
    platform = 'android';
    config;
    isInitialized = false;
    nextNotificationId = 1;
    constructor(config = {}) {
        this.config = {
            requestPermissionOnInit: true,
            enableSound: true,
            enableVibration: true,
            channelId: 'staychecked-notifications',
            channelName: 'StayChecked Notifications',
            channelDescription: 'Notifications for your habit challenges',
            importance: 'default',
            ...config
        };
    }
    async initialize() {
        if (this.isInitialized)
            return true;
        try {
            // Check if Capacitor and LocalNotifications are available
            if (!this.isSupported()) {
                console.warn('[AndroidNotificationWrapper] LocalNotifications not available');
                return false;
            }
            // Create notification channel for Android
            await this.createNotificationChannel();
            // Request permission if configured to do so
            if (this.config.requestPermissionOnInit) {
                const result = await this.requestPermission();
                if (!result.granted) {
                    console.warn('[AndroidNotificationWrapper] Permission not granted during init');
                }
            }
            // Set up event listeners
            this.setupEventListeners();
            this.isInitialized = true;
            console.log('[AndroidNotificationWrapper] Initialized successfully');
            return true;
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Initialization failed:', error);
            return false;
        }
    }
    async destroy() {
        if (!this.isInitialized)
            return;
        try {
            // Cancel all notifications
            await this.cancelAll();
            // Remove listeners
            await LocalNotifications.removeAllListeners();
            this.isInitialized = false;
            console.log('[AndroidNotificationWrapper] Destroyed');
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Error during destroy:', error);
        }
    }
    isSupported() {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }
    getPermissionStatus() {
        // Note: Capacitor doesn't provide a synchronous way to get permission status
        // This would need to be cached from the last permission check
        return 'default'; // Fallback - should be updated after permission checks
    }
    async requestPermission() {
        if (!this.isSupported()) {
            return { granted: false, permission: 'denied' };
        }
        try {
            const result = await LocalNotifications.requestPermissions();
            const granted = result.display === 'granted';
            const permission = granted ? 'granted' : 'denied';
            console.log(`[AndroidNotificationWrapper] Permission ${granted ? 'granted' : 'denied'}`);
            return { granted, permission: permission };
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Permission request failed:', error);
            return { granted: false, permission: 'denied' };
        }
    }
    async sendImmediate(payload) {
        if (!this.isSupported()) {
            throw new Error('LocalNotifications not supported on this platform');
        }
        const notificationId = this.getNextId();
        const notification = this.payloadToCapacitorNotification(payload, notificationId);
        try {
            await LocalNotifications.schedule({
                notifications: [notification]
            });
            console.log('[AndroidNotificationWrapper] Immediate notification sent:', payload.title);
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to send immediate notification:', error);
            throw error;
        }
    }
    async schedule(payload) {
        if (!this.isSupported()) {
            throw new Error('LocalNotifications not supported on this platform');
        }
        const notificationId = this.getNextId();
        const notification = this.payloadToCapacitorNotification(payload, notificationId, payload.scheduleTime);
        try {
            await LocalNotifications.schedule({
                notifications: [notification]
            });
            console.log(`[AndroidNotificationWrapper] Notification scheduled for ${payload.scheduleTime.toISOString()}: ${payload.title}`);
            return notificationId.toString();
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to schedule notification:', error);
            throw error;
        }
    }
    async cancel(id) {
        if (!this.isSupported())
            return;
        try {
            await LocalNotifications.cancel({
                notifications: [{ id: parseInt(id, 10) }]
            });
            console.log(`[AndroidNotificationWrapper] Cancelled notification: ${id}`);
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to cancel notification:', error);
        }
    }
    async cancelAll() {
        if (!this.isSupported())
            return;
        try {
            const pending = await LocalNotifications.getPending();
            if (pending.notifications.length > 0) {
                await LocalNotifications.cancel({
                    notifications: pending.notifications.map(n => ({ id: n.id }))
                });
            }
            console.log('[AndroidNotificationWrapper] All notifications cancelled');
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to cancel all notifications:', error);
        }
    }
    async cancelByTag(tag) {
        if (!this.isSupported())
            return;
        try {
            const pending = await LocalNotifications.getPending();
            const toCancel = pending.notifications
                .filter(notification => notification.extra?.tag === tag)
                .map(notification => ({ id: notification.id }));
            if (toCancel.length > 0) {
                await LocalNotifications.cancel({ notifications: toCancel });
            }
            console.log(`[AndroidNotificationWrapper] Cancelled ${toCancel.length} notifications with tag: ${tag}`);
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to cancel notifications by tag:', error);
        }
    }
    async getPending() {
        if (!this.isSupported())
            return [];
        try {
            const result = await LocalNotifications.getPending();
            return result.notifications.map(this.capacitorNotificationToPayload);
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to get pending notifications:', error);
            return [];
        }
    }
    async createNotificationChannel() {
        try {
            // Create notification channel for Android 8.0+
            await LocalNotifications.createChannel({
                id: this.config.channelId,
                name: this.config.channelName,
                description: this.config.channelDescription,
                importance: this.getChannelImportance(),
                visibility: 1, // Public
                sound: this.config.enableSound ? 'default' : undefined,
                vibration: this.config.enableVibration
            });
            console.log(`[AndroidNotificationWrapper] Notification channel created: ${this.config.channelId}`);
        }
        catch (error) {
            console.error('[AndroidNotificationWrapper] Failed to create notification channel:', error);
        }
    }
    getChannelImportance() {
        switch (this.config.importance) {
            case 'high': return 4;
            case 'default': return 3;
            case 'low': return 2;
            case 'min': return 1;
            default: return 3;
        }
    }
    getNextId() {
        return this.nextNotificationId++;
    }
    payloadToCapacitorNotification(payload, id, scheduleTime) {
        const notification = {
            id,
            title: payload.title,
            body: payload.body,
            sound: this.config.enableSound ? (payload.sound || 'default') : undefined,
            channelId: this.config.channelId,
            extra: {
                ...payload.data,
                tag: payload.tag,
                originalPayload: payload
            }
        };
        // Add scheduling if specified
        if (scheduleTime) {
            notification.schedule = {
                at: scheduleTime
            };
        }
        // Add small icon if available
        if (payload.icon) {
            notification.smallIcon = payload.icon;
        }
        // Add large icon if available  
        if (payload.image) {
            notification.largeIcon = payload.image;
        }
        return notification;
    }
    capacitorNotificationToPayload(notification) {
        const extra = notification.extra || {};
        return {
            id: notification.id?.toString(),
            title: notification.title || '',
            body: notification.body || '',
            icon: notification.smallIcon,
            image: notification.largeIcon,
            tag: extra.tag,
            data: extra,
            sound: notification.sound,
            scheduleTime: notification.schedule?.at ? new Date(notification.schedule.at) : new Date(),
            challengeId: extra.challengeId
        };
    }
    setupEventListeners() {
        // Listen for notification clicks
        LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
            const payload = notification.notification.extra?.originalPayload;
            if (payload) {
                const event = {
                    type: 'notification-click',
                    payload,
                    platform: this.platform
                };
                window.dispatchEvent(new CustomEvent('notification-click', { detail: event }));
            }
            console.log('[AndroidNotificationWrapper] Notification clicked:', notification.notification.title);
        });
        // Listen for notification received (when app is in foreground)
        LocalNotifications.addListener('localNotificationReceived', (notification) => {
            const payload = notification.extra?.originalPayload;
            if (payload) {
                const event = {
                    type: 'notification-received',
                    payload,
                    platform: this.platform
                };
                window.dispatchEvent(new CustomEvent('notification-received', { detail: event }));
            }
            console.log('[AndroidNotificationWrapper] Notification received:', notification.title);
        });
    }
}
